import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import type { TeamInvite, SendInviteRequest } from '@mockd/shared';
import { api, ApiError } from '../utils/apiClient';

interface UseTeamInvitesReturn {
  invites: TeamInvite[];
  loading: boolean;
  fetchInvites: (teamId: string) => Promise<void>;
  sendInvite: (teamId: string, body: SendInviteRequest) => Promise<void>;
  revokeInvite: (teamId: string, inviteId: string) => Promise<void>;
}

export function useTeamInvites(): UseTeamInvitesReturn {
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchInvites = useCallback(async (teamId: string) => {
    setLoading(true);
    try {
      const result = await api.get<{ data: TeamInvite[] }>(`/api/teams/${teamId}/invites`);
      setInvites(result.data);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load invites';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const sendInvite = useCallback(async (teamId: string, body: SendInviteRequest) => {
    try {
      const result = await api.post<{ data: TeamInvite }>(`/api/teams/${teamId}/invites`, body);
      // Replace any existing pending invite for the same email (resend), then prepend.
      setInvites((prev) => [
        result.data,
        ...prev.filter((i) => i.email !== result.data.email),
      ]);
      toast.success(`Invite sent to ${result.data.email}`);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to send invite';
      toast.error(message);
      throw err;
    }
  }, []);

  const revokeInvite = useCallback(async (teamId: string, inviteId: string) => {
    try {
      await api.del(`/api/teams/${teamId}/invites/${inviteId}`);
      setInvites((prev) => prev.filter((i) => i.id !== inviteId));
      toast.success('Invite revoked');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to revoke invite';
      toast.error(message);
      throw err;
    }
  }, []);

  return { invites, loading, fetchInvites, sendInvite, revokeInvite };
}
